/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { isError } from "@epic-mw/error-types";
import useSWRImmutable from "swr/immutable";

import { useAccountId } from "../account/account.hooks";
import { useApiConfig } from "../api-config";
import { getApiPath } from "../utils";

/**
 * The three states an AODC consent lookup can really be in.
 *
 * The old shape was `{ AODCFailed: boolean }`, which lied at runtime: a failed
 * request left it `undefined`, and every consumer wrote
 * `accountPolicy?.AODCFailed || false`, turning "we do not know" into "consent
 * passed". That is the fail-open MW-13431 exists to close, and a boolean cannot
 * express it. `unknown` is a first-class state here so no consumer can drop it by
 * accident.
 */
export type AodcPolicyStatus = "passed" | "failed" | "unknown";

export interface AodcPolicyRes {
	status: AodcPolicyStatus;
}

/**
 * Metric name for AODC lookup outcomes. Every lookup is recorded, split by a
 * `status` attribute of `passed`, `failed` or `unknown`, so the indeterminate
 * SHARE is computable. A counter that only fired on `unknown` gives a numerator
 * with no denominator, and a traffic increase then looks identical to a service
 * degradation.
 *
 * REVERT TRIGGER: `unknown` sustained above ~1% of total
 * `cosmos.policy.aodc_lookup` increments, or a sharp step up in that share against
 * its own baseline. Both are answerable from this one counter.
 *
 * ROLLBACK: in `NewsletterSignup.section.tsx`, change the gate from
 * `status !== "passed"` back to `status === "failed"`. That restores serving on an
 * indeterminate result, i.e. it re-opens the fail-open deliberately and
 * temporarily, and it is a one-line change with no data migration. Cvent already
 * behaves that way by design, so it needs no rollback.
 */
export const AODC_LOOKUP_METRIC = "policy.aodc.lookup";

/**
 * Record one lookup outcome. Fire-and-forget on purpose: the consent gate must
 * never depend on the metrics endpoint being reachable, so every failure here is
 * swallowed.
 *
 * `reason` rides along for logs. It is deliberately not a metric attribute — it is
 * unbounded, and the server drops it for that reason.
 */
const reportLookup = (serverUrl: string | undefined, status: AodcPolicyStatus, reason?: string): void => {
	try {
		void fetch(`${getApiPath(serverUrl)}/metrics`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				name: AODC_LOOKUP_METRIC,
				value: 1,
				route: typeof window === "undefined" ? "/" : window.location.pathname,
				status,
				reason,
			}),
		}).catch(() => undefined);
	} catch {
		// Never let telemetry break the gate.
	}
};

const getAODCPolicy = async (serverUrl?: string): Promise<AodcPolicyRes> => {
	try {
		const response = await fetch(`${getApiPath(serverUrl)}/policy/aodc`);

		// The nine-month defect: this check did not exist, so a 404 or 500 body was
		// parsed anyway and `json.failed` came back undefined.
		if (!response.ok) {
			reportLookup(serverUrl, "unknown", `http-${response.status}`);
			return { status: "unknown" };
		}

		const json = await response.json();

		if (typeof json?.failed !== "boolean") {
			reportLookup(serverUrl, "unknown", "malformed-body");
			return { status: "unknown" };
		}

		const status: AodcPolicyStatus = json.failed ? "failed" : "passed";
		reportLookup(serverUrl, status);
		return { status };
	} catch (err) {
		console.error("getAODCPolicy() failed to load", isError(err) && err.message);
		reportLookup(serverUrl, "unknown", "network-error");
		return { status: "unknown" };
	}
};

interface HookRes {
	result: AodcPolicyRes | undefined;
	isLoading: boolean;
	error: any;
}

export const useAccountAODCPolicy = (): HookRes => {
	const accountId = useAccountId();
	const { serverUrl } = useApiConfig();
	const shouldFetch = Boolean(accountId);

	const { data, error } = useSWRImmutable(
		shouldFetch ? [`aodc-${accountId}`, serverUrl] : null,
		(_key, url) => getAODCPolicy(url),
		{ errorRetryCount: 0 },
	);

	return {
		result: data,
		isLoading: Boolean(!data && !error && shouldFetch),
		error,
	};
};
